import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Heart, Sparkles, MessageSquare, ShieldCheck, ThumbsUp, ThumbsDown, Send, Inbox, CheckCircle2,
} from "lucide-react";
import { SEED_SUGGESTIONS } from "../../data/suggestions";
import {
  resolveClientQuery,
  queueForDesigner,
  type ClientReply,
  type AgentTraceStep,
} from "../../lib/clientAgent";
import { cls } from "../../lib/cls";

interface ClientCopilotProps {
  clientName?: string;
}

const PROMPTS = [
  { id: "status",   label: "How's my project looking?" },
  { id: "cost",     label: "Why did the cost change?" },
  { id: "timeline", label: "Will we hit the handover date?" },
  { id: "render",   label: "Can I see the latest render?" },
];

interface Bubble {
  id: string;
  role: "client" | "agent";
  text: string;
  reply?: ClientReply;
  streamedTrace?: AgentTraceStep[];
  streamingDone?: boolean;
  forUserQuery?: string;
  isSuggestionAck?: boolean;
}

export function ClientCopilot({ clientName = "Anika & Rohan" }: ClientCopilotProps): JSX.Element {
  const [bubbles, setBubbles] = useState<Bubble[]>([
    {
      id: "b0",
      role: "agent",
      text: `Hi ${clientName.split(" & ")[0] ?? clientName} — ask me anything in plain English. I answer from your project graph. If I can't, you can send it to Maya's inbox with one click.`,
      streamingDone: true,
    },
  ]);
  const [draft, setDraft] = useState("");
  const [thinking, setThinking] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [bubbles, thinking]);

  function ask(q: string): void {
    const text = q.trim();
    if (!text) return;
    setBubbles((b) => [...b, { id: `c-${Date.now()}`, role: "client", text }]);
    setDraft("");
    setThinking(true);

    const reply = resolveClientQuery(text);
    const bubbleId = `a-${Date.now()}`;
    setBubbles((b) => [
      ...b,
      { id: bubbleId, role: "agent", text: "", reply, streamedTrace: [], streamingDone: false, forUserQuery: text },
    ]);

    const STEP_MS = 220;
    const steps = reply.trace;
    let idx = 0;
    function streamNext(): void {
      if (idx >= steps.length) {
        setBubbles((b) => b.map((x) => x.id === bubbleId ? { ...x, text: reply.text, streamingDone: true } : x));
        setThinking(false);
        return;
      }
      const nextStep = steps[idx];
      if (!nextStep) { idx += 1; streamNext(); return; }
      setBubbles((b) => b.map((x) => x.id === bubbleId ? { ...x, streamedTrace: [...(x.streamedTrace ?? []), nextStep] } : x));
      idx += 1;
      setTimeout(streamNext, STEP_MS);
    }
    setTimeout(streamNext, 250);
  }

  function sendToInbox(b: Bubble): void {
    const original = b.forUserQuery ?? b.text;
    queueForDesigner(original);
    setBubbles((cur) => cur.map((x) =>
      x.id === b.id
        ? { ...x, isSuggestionAck: true, text: `${x.text}\n\nSent to Maya's inbox. She'll respond with the budget, timeline and style impact from your project.` }
        : x,
    ));
  }

  return (
    <div className="panel p-0 overflow-hidden flex flex-col h-[640px]">
      {/* Header */}
      <header className="px-4 py-3 border-b border-border flex items-center gap-2 shrink-0">
        <span className="w-7 h-7 rounded-lg bg-foyr text-cream grid place-items-center">
          <Heart className="w-3.5 h-3.5" />
        </span>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-ink-950 leading-tight">Your home build</div>
          <div className="text-[11px] text-ink-500 leading-tight">{clientName} · grounded in your project's graph</div>
        </div>
        <span className="ml-auto pill bg-fern/10 text-fern">
          <Sparkles className="w-3 h-3" /> online
        </span>
      </header>

      {/* Chat — primary surface */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-cream-deeper/30">
        <AnimatePresence initial={false}>
          {bubbles.map((b) => (
            <motion.div
              key={b.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className={cls("flex", b.role === "client" ? "justify-end" : "justify-start")}
            >
              <div className={cls(
                "max-w-[88%] rounded-2xl px-3 py-2 text-[12.5px] leading-snug",
                b.role === "client"
                  ? "bg-ink-950 text-cream rounded-tr-md"
                  : b.reply?.refusal
                    ? "bg-cream-dark border border-border text-ink-700 rounded-tl-md italic"
                    : b.isSuggestionAck
                      ? "bg-foyr/5 border border-foyr/30 text-ink-950 rounded-tl-md"
                      : "bg-white border border-border text-ink-950 rounded-tl-md",
              )}>
                {b.isSuggestionAck ? (
                  <div className="flex items-start gap-1.5">
                    <Inbox className="w-3.5 h-3.5 text-foyr-deep shrink-0 mt-0.5" />
                    <span className="whitespace-pre-line">{b.text}</span>
                  </div>
                ) : (
                  <span className="whitespace-pre-line">{b.text}</span>
                )}
                {b.streamedTrace && b.streamedTrace.length > 0 ? (
                  <ul className="mt-2 space-y-0.5 pl-2 border-l border-border text-[10px]">
                    {b.streamedTrace.map((s, i) => (
                      <motion.li
                        key={i}
                        initial={{ opacity: 0, x: -4 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.18 }}
                      >
                        <span className={cls(
                          "uppercase tracking-wider mr-1 text-[9px] font-mono",
                          s.kind === "think" ? "text-sea" : s.kind === "act" ? "text-foyr-deep" : "text-fern",
                        )}>{s.kind}</span>
                        <span className="text-ink-700">{s.text}</span>
                      </motion.li>
                    ))}
                  </ul>
                ) : null}
                {b.reply && b.streamingDone && b.reply.citations.length > 0 ? (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {b.reply.citations.map((c) => (
                      <span key={c.id} className="pill bg-cream-dark border border-border text-ink-700 text-[10px]">
                        <span className="font-mono text-[9px] text-ink-500">{c.kind}</span>
                        {c.label}
                      </span>
                    ))}
                  </div>
                ) : null}
                {b.reply?.refusal && b.streamingDone && !b.isSuggestionAck ? (
                  <button
                    onClick={() => sendToInbox(b)}
                    className="mt-2 btn btn-outline !text-[10px] !py-1 !px-2"
                  >
                    <Inbox className="w-3 h-3" /> Send this to Maya's inbox
                  </button>
                ) : null}
                {b.isSuggestionAck ? (
                  <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-fern">
                    <CheckCircle2 className="w-3 h-3" /> queued
                  </div>
                ) : null}
              </div>
            </motion.div>
          ))}
          {thinking ? (
            <motion.div key="thinking" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex justify-start">
              <div className="bg-white border border-border rounded-2xl rounded-tl-md px-3 py-2 text-[11px] text-ink-500 inline-flex items-center gap-1.5">
                <Dot /><Dot d="120ms" /><Dot d="240ms" />
                <span className="ml-1 italic">checking your project…</span>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>

      {/* Packets — collapsible, kept out of the chat scroll so order stays clean */}
      <ClientPacketsAccordion />

      {/* Input bar — always sticky at the bottom */}
      <div className="px-3 py-2 border-t border-border bg-cream shrink-0">
        <div className="flex gap-1.5 flex-wrap mb-2">
          {PROMPTS.map((p) => (
            <button
              key={p.id}
              onClick={() => ask(p.label)}
              className="pill bg-cream-dark border border-border text-ink-700 hover:border-foyr/40 hover:text-ink-950 transition"
            >
              <MessageSquare className="w-3 h-3" />{p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); ask(draft); } }}
            placeholder="Ask a question · or 'change …' / 'I want …' to queue a request"
            className="flex-1 text-[13px] bg-white border border-border rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-foyr/30"
          />
          <button onClick={() => ask(draft)} disabled={!draft.trim() || thinking} className="btn btn-foyr !px-3 !py-2">
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Dot({ d = "0ms" }: { d?: string }): JSX.Element {
  return <span className="w-1.5 h-1.5 rounded-full bg-foyr animate-pulse" style={{ animationDelay: d }} />;
}

/* ─── Packets accordion — between chat and input, collapsed by default ─── */

function ClientPacketsAccordion(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<Record<string, "accepted" | "rejected" | undefined>>({});
  const packets = SEED_SUGGESTIONS.filter((s) => s.kind === "substitute" || s.kind === "approval-packet").slice(0, 3);
  const pendingCount = packets.filter((p) => !state[p.id]).length;
  return (
    <div className="border-t border-border bg-cream-deeper/40 shrink-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-cream-dark/40 transition"
      >
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 rounded-md bg-foyr text-cream grid place-items-center"><ShieldCheck className="w-3 h-3" /></span>
          <span className="text-[12px] font-semibold text-ink-950">For your approval</span>
          {pendingCount > 0 ? (
            <span className="pill bg-foyr text-cream tabular text-[10px]">{pendingCount}</span>
          ) : (
            <span className="pill bg-fern/10 text-fern text-[10px]">all clear</span>
          )}
        </div>
        <span className="text-[11px] text-ink-500">{open ? "Hide" : "Show"}</span>
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-3 space-y-2">
              {packets.map((p) => {
                const done = state[p.id];
                return (
                  <div key={p.id} className="bg-white border border-border rounded-xl p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-md bg-foyr text-cream grid place-items-center shrink-0">
                        <ShieldCheck className="w-3 h-3" />
                      </span>
                      <div className="text-[12.5px] font-medium text-ink-950 leading-tight">{p.title}</div>
                    </div>
                    <p className="text-[11px] text-ink-700 leading-snug">{p.rationale}</p>
                    {p.delta && p.delta.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {p.delta.map((d) => (
                          <span key={d.label} className="pill bg-cream-dark border border-border text-ink-700 text-[10px] tabular">{d.label}</span>
                        ))}
                      </div>
                    ) : null}
                    {done ? (
                      <div className={cls("text-[11px] text-center py-1", done === "accepted" ? "text-fern" : "text-foyr-deep")}>
                        Thanks — Maya will see your response.
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 pt-1">
                        <button
                          onClick={() => setState((s) => ({ ...s, [p.id]: "rejected" }))}
                          className="btn btn-outline !text-[10px] !py-1 flex-1"
                        >
                          <ThumbsDown className="w-3 h-3" /> Not for me
                        </button>
                        <button
                          onClick={() => setState((s) => ({ ...s, [p.id]: "accepted" }))}
                          className="btn btn-foyr !text-[10px] !py-1 flex-1"
                        >
                          <ThumbsUp className="w-3 h-3" /> Looks good
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
